import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import {
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { KypayApiClient } from "../api-client/api-client";
import { balanceOfABI } from "../constants";
import type {
  CreateWalletReq,
  CreateWalletResponse,
  CryptoAssetsResponse,
  FeedKypayBalanceCardSessioneResponse,
  FeedKypayBalanceCardSessionProps,
  FeedKypayBalanceProps,
  FeedKypayBalanceResponse,
  GenerateCryptoAmountQuoteProps,
  GenerateWalletDepositProps,
  GetUserWalletBalance as GetUserWalletBalanceResponse,
  GetWalletDataResponse,
  GetWalleTokenyTransactionResponse,
  GetWalletPolygon2LausanneResponse,
  Kypay,
  SchemaRequestCreateTravelRule,
  SearchIdentityParams,
  SearchOlkyPassIdentity,
  SendKypayToUserWalletProps,
  SendKypayToUserWalletReponse,
  SendToKypayProps,
  SendToKypayReponse,
  SendToKypayWallettReponse,
  TransactionResponse,
  TransactionStatus,
  TransferKypayAmountProps,
  TransferKypayAmountResponse,
  WalletData,
  walletsTravelRules,
  WithdrawKypayBalanceProps,
  WithdrawKypayBalanceResponse
} from "../types";
import { getWeb3Client } from "./use-web3";
import { DEFAULT_STALE_TIME } from "@/features/utils/fetch";
import { ApiError } from "@/utils/internal-code-handler";

export type UseKypayResponse = {
  kypayQuery: UseQueryResult<Kypay>;
  createUserWallet: UseMutationResult<CreateWalletResponse, Error, string, unknown>;
};

export function useKypay() {
  const { t } = useTranslation(["wallet"]);
  const queryClient = useQueryClient();
  const { leUuid } = useCompanyContext();
  const kypayQuery = (isAllowed?: boolean, uuid?: string) =>
    useQuery<Kypay>({
      queryKey: ["kypay_balance", uuid],
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: isAllowed,
      placeholderData: keepPreviousData,
      queryFn: async () => await KypayApiClient.getKypay(leUuid ?? undefined)
    });

  const cryptoAssetsQuery = (options?: UseQueryOptions<CryptoAssetsResponse, null>) =>
    useQuery<CryptoAssetsResponse, null>({
      queryKey: ["crypto", "assets"],
      queryFn: async () => await KypayApiClient.getCryptoAssets(),
      staleTime: DEFAULT_STALE_TIME,
      refetchOnMount: true,
      ...options
    });
  const travelRuleWalletsQuery = (options?: UseQueryOptions<walletsTravelRules[], null>) =>
    useQuery<walletsTravelRules[], null>({
      queryKey: leUuid ? ["wallet", "rules", leUuid] : ["wallet", "rules"],
      queryFn: async () =>
        (await KypayApiClient.getTravelRuleWalletsQuery(leUuid ?? undefined)).data ?? [],
      staleTime: DEFAULT_STALE_TIME,
      ...options
    });

  const kypayPrefetchQuery = async (page?: number, limit?: number) =>
    await queryClient.prefetchQuery<Kypay>({
      queryKey: ["kypay", page, limit],
      queryFn: async () => await KypayApiClient.getKypay(leUuid ?? undefined)
    });

  const getTransactions = (data: { page?: number; limit?: number }) =>
    useQuery<TransactionResponse, null, TransactionResponse>({
      queryKey: leUuid
        ? ["transactions", data.page, data.limit, leUuid]
        : ["transactions", data.page, data.limit],
      queryFn: async () =>
        await KypayApiClient.getTransaction(data.page, data.limit, leUuid ?? undefined)
    });

  const useUserWalletDataQuery = (
    contextLegalEntityUuid?: string,
    options?: UseQueryOptions<GetWalletDataResponse, ApiError, WalletData | undefined>
  ) => {
    return useQuery<GetWalletDataResponse, ApiError, WalletData | undefined>({
      queryKey: options?.queryKey ?? (leUuid ? ["walletData", leUuid] : ["walletData"]),
      queryFn: async () => await KypayApiClient.getUserWalletData(contextLegalEntityUuid),
      select: options?.select ? options?.select : (data) => (data.success ? data.data : undefined),
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
      ...options
    });
  };

  const userWalleTransactionstQuery = (
    options?: UseQueryOptions<GetWalleTokenyTransactionResponse, null>
  ) =>
    useQuery<GetWalleTokenyTransactionResponse, null>({
      queryKey: leUuid
        ? ["wallet", "walletTransactions", leUuid]
        : ["wallet", "walletTransactions"],
      queryFn: async () => await KypayApiClient.getUserWalletTransactions(leUuid ?? undefined),
      select: options?.select ? options?.select : (data) => data,
      ...options
    });

  const walletPolygon2LausanneQuery = (
    options?: UseQueryOptions<GetWalletPolygon2LausanneResponse, null>
  ) =>
    useQuery<GetWalletPolygon2LausanneResponse, null>({
      queryKey: ["wallet", "polygon2Lausanne"],
      queryFn: async () => await KypayApiClient.getWalletPolygon2Lausanne(),
      ...options
    });

  const createUserWalletMutation = (
    options?: UseMutationOptions<CreateWalletResponse, Error, CreateWalletReq>
  ) =>
    useMutation<CreateWalletResponse, Error, CreateWalletReq>({
      mutationFn: async (wallet: CreateWalletReq) =>
        KypayApiClient.createUserWallet(wallet, leUuid ?? undefined),
      ...options
    });

  const createTravelRuleWalletsMutation = (
    options?: UseMutationOptions<walletsTravelRules, Error, SchemaRequestCreateTravelRule>
  ) =>
    useMutation<walletsTravelRules, Error, SchemaRequestCreateTravelRule>({
      mutationFn: async (data: SchemaRequestCreateTravelRule) =>
        (await KypayApiClient.createTravelRuleWalletsQuery(data, leUuid ?? undefined)).data,
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: leUuid ? ["wallet", "rules", leUuid] : ["wallet", "rules"]
        }),
      ...options
    });
  const createUserOlkyWalletMutation = (
    options?: UseMutationOptions<CreateWalletResponse, Error>
  ) =>
    useMutation<CreateWalletResponse, Error>({
      mutationFn: async () => KypayApiClient.createUserOlkyWallet(),
      ...options
    });

  const sendKypayToUserWalletMutation = (
    options?: UseMutationOptions<SendKypayToUserWalletReponse, Error, SendKypayToUserWalletProps>
  ) =>
    useMutation<SendKypayToUserWalletReponse, Error, SendKypayToUserWalletProps>({
      mutationFn: async (sendData) =>
        KypayApiClient.sendKypayToUserWallet(sendData, leUuid ?? undefined),
      ...options
    });

  const investPartnerMutation = (
    options?: UseMutationOptions<SendKypayToUserWalletReponse, Error, SendKypayToUserWalletProps>
  ) =>
    useMutation<SendKypayToUserWalletReponse, Error, SendKypayToUserWalletProps>({
      mutationFn: async (sendData) => KypayApiClient.investToPartner(sendData, leUuid ?? undefined),
      ...options
    });

  const sendToKypayMutation = (
    options?: UseMutationOptions<
      SendToKypayWallettReponse | SendToKypayReponse,
      Error,
      SendToKypayProps
    >
  ) =>
    useMutation<SendToKypayWallettReponse | SendToKypayReponse, Error, SendToKypayProps>({
      mutationFn: async (sendData) => KypayApiClient.sendToKyPay(sendData, leUuid ?? undefined),
      ...options
    });
  const feedKypayBalanceMutation = (
    options?: UseMutationOptions<FeedKypayBalanceResponse, Error, FeedKypayBalanceProps>
  ) =>
    useMutation<FeedKypayBalanceResponse, Error, FeedKypayBalanceProps>({
      mutationFn: async (balanceData) => {
        const response = await KypayApiClient.feedKypayBalance(balanceData, leUuid ?? undefined);
        if (!response || !response?.url) {
          throw new Error(t("kypay.error.feed_kypay_balance"));
        }
        return response;
      },
      ...options
    });

  const feedKypayCryptoAmountQuoteMutation = (
    options?: UseMutationOptions<number, Error, GenerateCryptoAmountQuoteProps>
  ) =>
    useMutation<number, Error, GenerateCryptoAmountQuoteProps>({
      mutationFn: async (data) => {
        const response = await KypayApiClient.generateQuoteAmount(data, leUuid ?? undefined);
        if (!response) {
          throw new Error(t("kypay.error.feed_kypay_balance"));
        }
        return response;
      },

      ...options
    });

  const generateWalletAddressMutation = (
    options?: UseMutationOptions<string, Error, GenerateWalletDepositProps>
  ) =>
    useMutation<string, Error, GenerateWalletDepositProps>({
      mutationFn: async (data) => {
        const response = await KypayApiClient.generateDepositWallet(data, leUuid ?? undefined);
        if (!response.success) throw new Error(t("kypay.error.feed_depost_wallet_address"));
        return response.data;
      },
      ...options
    });
  const feedKypayBalanceCardSessionMutation = (
    options?: UseMutationOptions<
      FeedKypayBalanceCardSessioneResponse,
      Error,
      FeedKypayBalanceCardSessionProps
    >
  ) =>
    useMutation<FeedKypayBalanceCardSessioneResponse, Error, FeedKypayBalanceCardSessionProps>({
      mutationFn: async (balanceData) => {
        const response = await KypayApiClient.feedKypayBalanceCardSession(
          balanceData,
          leUuid ?? undefined
        );
        if (!response || !response?.session_id) {
          throw new Error(t("kypay.error.feed_kypay_balance_card_session"));
        }
        return response;
      },
      ...options
    });

  const getTransactionReferenceMutation = (
    options?: UseMutationOptions<
      TransactionStatus,
      Error,
      { transactionReference: string; contextLegalEntityUuid?: string }
    >
  ) =>
    useMutation<TransactionStatus, Error, { transactionReference: string }>({
      mutationFn: async (data) =>
        await KypayApiClient.getTransactionStatus(data?.transactionReference, leUuid ?? undefined),
      ...options
    });

  const withdrawKypayBalanceMutation = (
    options?: UseMutationOptions<WithdrawKypayBalanceResponse, Error, WithdrawKypayBalanceProps>
  ) =>
    useMutation<WithdrawKypayBalanceResponse, Error, WithdrawKypayBalanceProps>({
      mutationFn: async (balanceData) =>
        await KypayApiClient.withdrawKypayBalance(balanceData, leUuid ?? undefined),
      onSettled: () => queryClient.invalidateQueries({ queryKey: ["kypay"] }),
      ...options
    });

  const getUserWalletBalanceQuery = (
    data: { walletAddress: string; token: string },
    options?: Omit<UseQueryOptions<GetUserWalletBalanceResponse>, "queryKey">
  ) =>
    useQuery<GetUserWalletBalanceResponse>({
      queryKey: ["wallet", "user", "balance", data.walletAddress],
      queryFn: async () => {
        const web3Client = await getWeb3Client();
        const contract = new web3Client.eth.Contract(balanceOfABI, data.token);
        const resultInWei = (await contract.methods.balanceOf(data.walletAddress).call()) as number;
        const resultInEther = parseFloat(web3Client.utils.fromWei(resultInWei, "ether"));
        return { balanceWei: resultInWei, balanceEther: resultInEther };
      },
      ...options
    });
  const kypayAccountsQuery = () =>
    useQuery({
      queryKey: leUuid ? ["kypayAccounts", leUuid] : ["kypayAccounts"],
      queryFn: async () => await KypayApiClient.getKypayAccounts(leUuid ?? undefined),
      refetchOnWindowFocus: false,
      refetchOnMount: false
    });

  const useSearchIdentitiesMutation = (
    options?: UseMutationOptions<SearchOlkyPassIdentity[], Error, SearchIdentityParams>
  ) =>
    useMutation<SearchOlkyPassIdentity[], Error, SearchIdentityParams>({
      mutationFn: async (params: SearchIdentityParams) => {
        try {
          const res = await KypayApiClient.searchIdentities(params.keyword);
          return res;
        } catch (_) {
          return [];
        }
      },

      ...options
    });

  const transferKypayAmountMutation = (
    options?: UseMutationOptions<TransferKypayAmountResponse, Error, TransferKypayAmountProps>
  ) =>
    useMutation<TransferKypayAmountResponse, Error, TransferKypayAmountProps>({
      mutationFn: async (params) => {
        const response = await KypayApiClient.transferKypayAmount(params, leUuid ?? undefined);
        if (!response) {
          throw new Error(t("kypay.error.feed_kypay_balance"));
        }
        return response;
      },
      ...options
    });

  return {
    kypayQuery,
    getUserWalletBalanceQuery,
    createUserWalletMutation,
    createUserOlkyWalletMutation,
    sendKypayToUserWalletMutation,
    feedKypayBalanceMutation,
    feedKypayBalanceCardSessionMutation,
    withdrawKypayBalanceMutation,
    walletPolygon2LausanneQuery,
    sendToKypayMutation,
    kypayAccountsQuery,
    useSearchIdentitiesMutation,
    transferKypayAmountMutation,
    useUserWalletDataQuery,
    userWalleTransactionstQuery,
    kypayPrefetchQuery,
    investPartnerMutation,
    getTransactionReferenceMutation,
    getTransactions,
    feedKypayCryptoAmountQuoteMutation,
    cryptoAssetsQuery,
    generateWalletAddressMutation,
    travelRuleWalletsQuery,
    createTravelRuleWalletsMutation
  };
}
